import { useState, type FormEvent } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/dag41ahtto0ceqqnhlhg";

type Status = "idle" | "loading" | "success" | "error";

const INITIAL = { name: "", contact: "", email: "", type: "", message: "" };

export default function ContactSection() {
  const [values, setValues] = useState(INITIAL);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [okMessage, setOkMessage] = useState("");

  const update = (key: keyof typeof INITIAL) => (e: { target: { value: string } }) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus("loading");
    setFormError("");
    setOkMessage("");
    setErrors({});

    const form = event.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get("contact_alt") || "").trim();

    // 机器人命中：不发送任何请求，给出与正常提交一致的回执
    if (honeypot !== "") {
      setOkMessage("感谢你的来信！编辑部已收到你的信息，核实后会尽快与你联系。");
      setStatus("success");
      form.reset();
      setValues(INITIAL);
      return;
    }

    const nextErrors: Record<string, string> = {};
    if (!values.name.trim()) nextErrors.name = "请填写你的称呼";
    if (!values.contact.trim()) nextErrors.contact = "请填写手机号或微信号，便于我们回访";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) nextErrors.email = "请填写有效的联系邮箱";
    if (!values.type) nextErrors.type = "请选择来信类型";
    if (!values.message.trim()) nextErrors.message = "请填写内容";
    if (values.message.length > 500) nextErrors.message = "内容不能超过 500 字";
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setStatus("idle");
      return;
    }

    // 构建 urlencoded 载荷（以真实 DOM 表单为准，兼顾浏览器自动填充）
    const payload = new URLSearchParams();
    for (const [key, value] of formData.entries()) {
      if (key !== "contact_alt") payload.append(key, String(value));
    }

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam|form data is spam/i.test(serverMsg);

      if (!response.ok || !parsed?.code || parsed.code !== "OK" || isSpam) {
        setFormError(serverMsg && !isSpam ? serverMsg : "提交未成功，请稍后重试或直接邮件联系我们。");
        setStatus("error");
        return;
      }
      setOkMessage("感谢你的来信！编辑部已收到你的信息，核实后会尽快与你联系。");
      setStatus("success");
      form.reset();
      setValues(INITIAL);
    } catch {
      setFormError("网络异常，提交未成功，请稍后重试。");
      setStatus("error");
    }
  };

  return (
    <section className="border-t border-background-200 bg-background-100/60" id="contact">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-8 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-12">
        {/* 说明区 */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-2">
            <span className="h-5 w-1 rounded-full bg-primary-500" aria-hidden="true" />
            <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">线索征集 · 联系我们</h2>
          </div>
          <p className="mt-3 text-sm leading-7 text-foreground-600">
            如果你掌握正在发生的大事线索，或发现我们的报道需要更正，欢迎随时来信。编辑部将对每一条信息进行核实与回访，重要线索一经采用，会第一时间进入滚动快讯。
          </p>

          <div className="mt-6 space-y-4">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-primary-100 text-lg text-primary-600">
                <i className="ri-mail-line" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-950">来信邮箱</p>
                <p className="text-xs text-foreground-500">editor@shishibaoliao.example</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-accent-100 text-lg text-accent-700">
                <i className="ri-time-line" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-950">值班时间</p>
                <p className="text-xs text-foreground-500">编辑部全天候在线，热门时段 30 分钟内响应</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-secondary-100 text-lg text-secondary-700">
                <i className="ri-shield-check-line" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-950">信息保密</p>
                <p className="text-xs text-foreground-500">你的个人信息仅用于核实与回访，不会对外公开</p>
              </div>
            </div>
          </div>
        </div>

        {/* 表单区 */}
        <form
          id="news-form"
          data-readdy-form
          className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7 lg:col-span-7"
          onSubmit={handleSubmit}
          noValidate
        >
          {/* 防机器人字段 */}
          <div className="meta-aux" aria-hidden="true">
            <label htmlFor="aux">请勿填写</label>
            <input
              type="text"
              id="aux"
              name="contact_alt"
              tabIndex={-1}
              autoComplete="off"
              readOnly
              aria-hidden="true"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-900">
                你的称呼 <span className="text-primary-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={values.name}
                onChange={update("name")}
                placeholder="如何称呼你"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
              {errors.name && <p className="mt-1 text-xs text-primary-600">{errors.name}</p>}
            </div>
            <div>
              <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-900">
                联系邮箱 <span className="text-primary-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={values.email}
                onChange={update("email")}
                placeholder="name@example.com"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
              {errors.email && <p className="mt-1 text-xs text-primary-600">{errors.email}</p>}
            </div>
            <div>
              <label htmlFor="contact" className="mb-1.5 block text-sm font-medium text-foreground-900">
                手机号 / 微信号
              </label>
              <input
                type="text"
                id="contact"
                name="contact"
                value={values.contact}
                onChange={update("contact")}
                placeholder="便于编辑部回访"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <div>
              <label htmlFor="type" className="mb-1.5 block text-sm font-medium text-foreground-900">
                来信类型 <span className="text-primary-500">*</span>
              </label>
              <select
                id="type"
                name="type"
                value={values.type}
                onChange={update("type")}
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              >
                <option value="">请选择类型</option>
                <option value="新闻线索">提供新闻线索</option>
                <option value="投稿合作">投稿 / 合作</option>
                <option value="纠错反馈">报道纠错</option>
                <option value="意见建议">意见与建议</option>
              </select>
              {errors.type && <p className="mt-1 text-xs text-primary-600">{errors.type}</p>}
            </div>
          </div>

          <div className="mt-4">
            <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-900">
              内容描述 <span className="text-primary-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              rows={5}
              value={values.message}
              onChange={update("message")}
              maxLength={500}
              placeholder="请简要描述事件时间、地点、经过，或你希望反馈的内容……"
              className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-6 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
            <div className="mt-1 flex items-center justify-between">
              {errors.message ? (
                <p className="text-xs text-primary-600">{errors.message}</p>
              ) : (
                <span className="text-xs text-foreground-400">请控制在 500 字以内</span>
              )}
              <span className="font-label text-xs text-foreground-400">{values.message.length} / 500</span>
            </div>
          </div>

          {status === "error" && (
            <p className="mt-4 flex items-start gap-2 rounded-md bg-primary-100/70 px-3 py-2.5 text-sm text-primary-800">
              <i className="ri-error-warning-fill mt-0.5" aria-hidden="true" />
              {formError}
            </p>
          )}
          {status === "success" && (
            <p className="mt-4 flex items-start gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-sm text-secondary-900">
              <i className="ri-checkbox-circle-fill mt-0.5" aria-hidden="true" />
              {okMessage}
            </p>
          )}

          <button
            type="submit"
            disabled={status === "loading"}
            className="mt-5 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
          >
            {status === "loading" ? (
              <>
                <i className="ri-loader-4-line animate-spin" aria-hidden="true" /> 正在提交…
              </>
            ) : (
              <>
                <i className="ri-send-plane-fill" aria-hidden="true" /> 提交给编辑部
              </>
            )}
          </button>
          <p className="mt-3 text-xs text-foreground-400">
            提交即表示你同意我们仅为核实与回访而处理以上信息。
          </p>
        </form>
      </div>
    </section>
  );
}